import { isCurrentFollowUpDebrief } from "./debrief.ts";
import { nextStepLabel } from "./format.ts";
import { completeCaseNextActionWithManualNext, nextManualStepDraft } from "./nextActionEngine.ts";
import type { CaseStage, CaseStatus, FollowUp, RealEstateCase, TimelineActivity } from "./types.ts";
import type { NextStepDraft } from "./uiTypes.ts";
import { excludeCase } from "./waitingWorkflow.ts";

export const MEETING_OUTCOMES = [
  "Spolupráca dohodnutá",
  "Majiteľ si chce zatiaľ predávať sám",
  "Potrebuje čas na rozhodnutie",
  "Dohodnutý follow-up",
  "Pripraviť CMA",
  "Podpis sprostredkovateľskej zmluvy",
  "Fotenie nehnuteľnosti",
  "Zainzerovať",
  "Nezáujem / uzavrieť prípad"
] as const;

export type MeetingOutcome = (typeof MEETING_OUTCOMES)[number];

type MeetingOutcomeRule = {
  purpose?: string;
  stage?: CaseStage;
  status?: CaseStatus;
  closesCase?: boolean;
};

export const MEETING_OUTCOME_RULES: Record<MeetingOutcome, MeetingOutcomeRule> = {
  "Spolupráca dohodnutá": {
    purpose: "Dohodnúť ďalší postup spolupráce",
    stage: "Aktívne",
    status: "Aktívne"
  },
  "Majiteľ si chce zatiaľ predávať sám": {
    purpose: "Vyhodnotiť priebeh vlastného predaja",
    stage: "Follow-up",
    status: "Predáva sám"
  },
  "Potrebuje čas na rozhodnutie": {
    purpose: "Rozhodnutie o spolupráci",
    stage: "Follow-up",
    status: "Rozmýšľa"
  },
  "Dohodnutý follow-up": {
    purpose: "Nadviazať na dohodu zo stretnutia",
    stage: "Follow-up",
    status: "Follow-up"
  },
  "Pripraviť CMA": {
    purpose: "Pripraviť CMA",
    stage: "Aktívne",
    status: "Aktívne"
  },
  "Podpis sprostredkovateľskej zmluvy": {
    purpose: "Podpis sprostredkovateľskej zmluvy",
    stage: "Aktívne",
    status: "Aktívne"
  },
  "Fotenie nehnuteľnosti": {
    purpose: "Fotenie nehnuteľnosti",
    stage: "Aktívne",
    status: "Aktívne"
  },
  Zainzerovať: {
    purpose: "Zainzerovať nehnuteľnosť",
    stage: "Aktívne",
    status: "Aktívne"
  },
  "Nezáujem / uzavrieť prípad": {
    closesCase: true
  }
};

export const meetingOutcomeNextStep = (outcome: MeetingOutcome, now = new Date()): NextStepDraft | undefined => {
  const rule = MEETING_OUTCOME_RULES[outcome];
  if (!rule.purpose) return undefined;
  return { ...nextManualStepDraft(now), purpose: rule.purpose };
};

const activity = (title: string, body: string, createdAt: string, followUp?: FollowUp): TimelineActivity => ({
  id: `activity-meeting-outcome-${createdAt.replace(/[^0-9]/g, "")}`,
  createdAt,
  type: "debrief",
  title,
  body,
  followUp
});

export const applyManualMeetingOutcome = (
  caseItem: RealEstateCase,
  reminderId: string,
  outcome: MeetingOutcome,
  nextStep: NextStepDraft | undefined,
  completedAt: string
): RealEstateCase => {
  const reminder = caseItem.debriefReminders?.find((item) => item.id === reminderId);
  if (!reminder || reminder.status === "completed" || reminder.status === "not_required") return caseItem;

  const rule = MEETING_OUTCOME_RULES[outcome];
  const completedReminders = caseItem.debriefReminders?.map((item) => item.id !== reminderId ? item : {
    ...item,
    status: "completed" as const,
    completedAt,
    updatedAt: completedAt
  });

  if (rule.closesCase) {
    const excluded = excludeCase({ ...caseItem, debriefReminders: completedReminders }, "Nezáujem po stretnutí", completedAt);
    return {
      ...excluded,
      timeline: [
        activity(`Výsledok stretnutia: ${outcome}`, "Prípad bol uzavretý cez existujúci archívny flow.", completedAt),
        ...excluded.timeline
      ]
    };
  }

  if (!nextStep?.date || !nextStep.time || !nextStep.purpose.trim()) return caseItem;
  const followUp: FollowUp = {
    date: nextStep.date,
    time: nextStep.time,
    ...(nextStep.endTime ? { endTime: nextStep.endTime } : {}),
    purpose: nextStep.purpose.trim()
  };
  const completion = isCurrentFollowUpDebrief(caseItem, reminder)
    ? completeCaseNextActionWithManualNext(caseItem, followUp, completedAt)
    : null;
  const updatedCase = completion?.caseItem ?? {
    ...caseItem,
    followUp,
    nextStep: nextStepLabel(followUp),
    updatedAt: completedAt
  };

  return {
    ...updatedCase,
    stage: rule.stage ?? updatedCase.stage,
    status: rule.status ?? updatedCase.status,
    debriefReminders: completedReminders,
    timeline: [
      activity(
        `Výsledok stretnutia: ${outcome}`,
        `Manuálne uzavreté. Ďalší krok: ${nextStepLabel(followUp)}.`,
        completedAt,
        followUp
      ),
      ...updatedCase.timeline
    ],
    updatedAt: completedAt
  };
};
